"use client";

import { faCartCirclePlus, faCartCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import {
	Button,
	Dialog,
	type DialogProps,
	RadixSelect,
	RadixSelectItem,
	Tooltip,
	Typography,
	Wrap,
	cn,
	useModal,
} from "@tosize/ui";
import { type ComponentProps, Fragment, useCallback, useState } from "react";
import type { AddToCartMessagesType } from "~/app/[lang]/cart/utils/getAddToCartMessages";
import { useAddToCart } from "~/app/[lang]/cart/utils/useAddToCart";
import { MediaButton } from "../media/Buttons";

interface Sample {
	productId: string;
	sampleProductId?: string;
	name: string;
}
interface SampleButtonProps {
	lang: string;
	productId: string;
	samples: Sample[];
	component?: "Button" | "MediaButton";
	componentType?: "default" | "multiple";
	className?: ComponentProps<"button">["className"];
	cartMessages: AddToCartMessagesType;
	messages: {
		title: string;
		notAvailable: string;
		content: string;
	};
}

export function SampleButton(props: SampleButtonProps) {
	const {
		lang,
		samples,
		productId,
		component = "Button",
		componentType = "default",
		messages,
		cartMessages,
		className,
	} = props;
	const { handleAddItem, addingItem } = useAddToCart(cartMessages);
	const dialog = useModal();

	const showButton = samples.length > 0;

	const onClickCallback = useCallback(async () => {
		if (componentType === "multiple") {
			return dialog.toggle();
		}

		const sample = samples.at(0);

		if (sample?.sampleProductId !== undefined) {
			return await handleAddItem({
				lang,
				cartItem: {
					id: sample.sampleProductId,
					quantity: 1,
					data: JSON.stringify({ product: sample.sampleProductId }),
				},
			});
		}
	}, [componentType, dialog, handleAddItem, lang, samples]);

	return (
		<>
			{component === "MediaButton" ? (
				<MediaButton
					className={cn({ "opacity-70": !showButton })}
					icon={!showButton ? faCartCircleXmark : faCartCirclePlus}
					title={messages.title}
					isLoading={!!addingItem}
					onClick={onClickCallback}
					tooltip={!showButton ? messages.notAvailable : undefined}
				/>
			) : (
				<Wrap
					if={!showButton}
					with={(children) => (
						<Tooltip content={messages.notAvailable} color="shades-light">
							{children}
						</Tooltip>
					)}
				>
					<Button
						className={cn(className, { "opacity-70": !showButton })}
						starticon={!showButton ? faCartCircleXmark : faCartCirclePlus}
						variant="link"
						isLoading={!!addingItem}
						onClick={onClickCallback}
					>
						{messages.title}
					</Button>
				</Wrap>
			)}

			{componentType === "multiple" && showButton && (
				<MultipleSampleDialog
					lang={lang}
					cartMessages={cartMessages}
					messages={messages}
					samples={samples}
					productId={productId}
					{...dialog.props}
				/>
			)}
		</>
	);
}

interface MultipleSampleDialogProps
	extends Pick<SampleButtonProps, "samples" | "productId" | "lang" | "cartMessages" | "messages">,
		Omit<DialogProps, "title"> {}

function MultipleSampleDialog({
	lang,
	cartMessages,
	messages,
	samples,
	productId,
	...props
}: MultipleSampleDialogProps) {
	const currentSampleProduct = samples.find((item) => item.productId === productId);
	const [value, setValue] = useState<string | undefined>(currentSampleProduct?.sampleProductId);
	const { handleAddItem, addingItem } = useAddToCart(cartMessages);

	return (
		<Dialog title={messages.title} {...props}>
			<div className="flex flex-col gap-4">
				<Typography variant="paragraph">{messages.content}</Typography>

				<RadixSelect
					title={messages.title}
					value={value}
					onValueChange={(value) => setValue(value)}
				>
					{samples.map((item) => (
						<Fragment key={item.sampleProductId}>
							{item.sampleProductId ? (
								<RadixSelectItem value={item.sampleProductId}>{item.name}</RadixSelectItem>
							) : null}
						</Fragment>
					))}
				</RadixSelect>

				<Button
					starticon={faCartCirclePlus}
					variant="outlined"
					size="large"
					isLoading={!!addingItem}
					onClick={async () => {
						if (value) {
							await handleAddItem({
								lang,
								cartItem: {
									id: value,
									quantity: 1,
									data: JSON.stringify({ product: value }),
								},
							});

							if (props.onOpenChange) props.onOpenChange(false);
						}
					}}
				>
					{messages.title}
				</Button>
			</div>
		</Dialog>
	);
}
